/**
 * AudioClip
 *
 * Represents a loaded audio asset (decoded AudioBuffer).
 * Handles decoding, caching, and metadata extraction.
 *
 * @module @engine/audio/AudioClip
 */

import type { AudioClipHandle } from './types';

export interface AudioClipMetadata {
  duration: number;
  channels: number;
  sampleRate: number;
  /** Size of the raw audio data in bytes */
  rawSize: number;
  /** Size of the compressed source in bytes */
  compressedSize: number;
}

/**
 * Manages audio clip loading and decoding.
 */
export class AudioClip {
  private _handle: AudioClipHandle;
  private _rawData: ArrayBuffer | null = null;

  constructor(guid: string) {
    this._handle = {
      guid,
      buffer: null,
      state: 'unloaded',
      duration: 0,
      channels: 0,
      sampleRate: 0,
    };
  }

  get guid(): string { return this._handle.guid; }
  get buffer(): AudioBuffer | null { return this._handle.buffer; }
  get state(): AudioClipHandle['state'] { return this._handle.state; }
  get duration(): number { return this._handle.duration; }
  get channels(): number { return this._handle.channels; }
  get sampleRate(): number { return this._handle.sampleRate; }
  get error(): string | undefined { return this._handle.error; }
  get handle(): Readonly<AudioClipHandle> { return this._handle; }
  get rawData(): ArrayBuffer | null { return this._rawData; }

  /**
   * Get metadata about this clip
   */
  getMetadata(): AudioClipMetadata {
    return {
      duration: this._handle.duration,
      channels: this._handle.channels,
      sampleRate: this._handle.sampleRate,
      rawSize: this._handle.buffer
        ? this._handle.buffer.length * this._handle.buffer.numberOfChannels * 4
        : 0,
      compressedSize: this._rawData?.byteLength ?? 0,
    };
  }

  /**
   * Load and decode audio data.
   *
   * @param data Raw audio file data (mp3, ogg, wav, etc.)
   * @param ctx AudioContext to use for decoding
   */
  async load(data: ArrayBuffer, ctx: AudioContext): Promise<void> {
    if (this._handle.state === 'loading') return;

    this._handle.state = 'loading';
    this._rawData = data;

    try {
      const buffer = await ctx.decodeAudioData(data.slice(0)); // slice to avoid detach
      this._handle.buffer = buffer;
      this._handle.duration = buffer.duration;
      this._handle.channels = buffer.numberOfChannels;
      this._handle.sampleRate = buffer.sampleRate;
      this._handle.state = 'loaded';
      this._handle.error = undefined;
    } catch (err) {
      this._handle.state = 'error';
      this._handle.error = err instanceof Error ? err.message : String(err);
      throw err;
    }
  }

  /**
   * Unload the audio buffer to free memory.
   */
  unload(): void {
    this._handle.buffer = null;
    this._handle.state = 'unloaded';
    this._handle.duration = 0;
    this._handle.channels = 0;
    this._handle.sampleRate = 0;
    this._rawData = null;
  }

  /**
   * Generate a waveform preview (for visualization in the editor).
   * Returns an array of normalized peak values.
   *
   * @param resolution Number of samples in the waveform
   */
  generateWaveform(resolution: number = 256): Float32Array {
    const buffer = this._handle.buffer;
    if (!buffer) return new Float32Array(resolution);

    const result = new Float32Array(resolution);
    const channelData = buffer.getChannelData(0);
    const samplesPerBin = Math.floor(channelData.length / resolution);

    for (let i = 0; i < resolution; i++) {
      let peak = 0;
      const start = i * samplesPerBin;
      const end = Math.min(start + samplesPerBin, channelData.length);
      for (let j = start; j < end; j++) {
        const abs = Math.abs(channelData[j]);
        if (abs > peak) peak = abs;
      }
      result[i] = peak;
    }

    return result;
  }
}
